import { useCallback, useEffect, useMemo, useReducer } from 'react';
import type { FormEvent } from 'react';
import { FieldWrapper, PasswordRequirement, Valid } from './styled';
import { Textfield, useIsMounted } from '@simplywallst/ui-core';
import { Button } from '@simplywallst/ui-core';
import { validateEmailAddress } from '@utilities/validation';
import { produce } from 'immer';
import If from '@components/If';
import { useRegister } from './hooks/useRegister';
import { useHotjar } from '@/hooks/useHotjar';
import { useDispatch, useSelector } from 'react-redux';
import { getLocation } from '@/redux/location/selectors';
import { ROUTE_COMPANY, ROUTE_COMPANY_WITH_EVENTS } from '@/constants/routes';
import { useTracking } from 'react-tracking';
import { useRewardfulData } from '@/features/rewardful';
import loadable from '@loadable/component';
import { useTranslation } from '@/hooks/useTranslation';
import { z } from 'zod';

const AuthenticationErrorMessages = loadable(
  () => import('../AuthenticationErrorMessages'),
  {
    resolveComponent: (c) => c.AuthenticationErrorMessages,
  }
);

interface Props {
  disabled?: boolean;
  /**
   * Set redirect URL for once post-verification registration has occurred.
   */
  postVerifyRedirectTo?: string;
  onRegister?: () => void;
}

type FormState = Record<
  string,
  {
    value: string;
    errorMessage?: string;
    state: 'editing' | 'valid' | 'error';
  }
>;

const getInitialFormState = (
  initialEmail: string | null | undefined
): FormState =>
  ({
    email: {
      value: initialEmail ?? '',
      errorMessage: '',
      state: 'editing',
    },
    password: {
      value: '',
      errorMessage: '',
      state: 'editing',
    },
    firstName: {
      value: '',
      state: 'editing',
    },
  }) as const;

type Action =
  | { type: 'editEmail'; value: string }
  | { type: 'emailValidation'; value: string }
  | { type: 'editPassword'; value: string }
  | { type: 'passwordValidation'; value: string }
  | { type: 'editName'; value: string };

function reducer(state: FormState, action: Action) {
  return produce(state, (draft) => {
    switch (action.type) {
      case 'editEmail':
        draft.email.value = action.value;
        draft.email.errorMessage = '';
        draft.email.state = 'editing';
        break;
      case 'emailValidation':
        draft.email.errorMessage = action.value;
        if (draft.email.value === '') {
          draft.email.state = 'editing';
        } else {
          draft.email.state = action.value ? 'error' : 'valid';
        }
        break;
      case 'editPassword':
        draft.password.value = action.value;
        draft.password.errorMessage = '';
        draft.password.state = 'editing';
        break;
      case 'passwordValidation':
        draft.password.errorMessage = action.value;
        if (draft.password.value === '') {
          draft.password.state = 'editing';
        } else {
          draft.password.state = action.value ? 'error' : 'valid';
        }
        break;
      case 'editName':
        draft.firstName.value = action.value;
        if (action.value === '') {
          draft.firstName.state = 'editing';
        } else {
          draft.firstName.state = 'valid';
        }
        break;
    }
  });
}

const companyPageType = ROUTE_COMPANY || ROUTE_COMPANY_WITH_EVENTS;

export const RegistrationForm = (props: Props) => {
  const [hotjar] = useHotjar();
  const location = useSelector(getLocation);
  const initialFormState = useMemo(
    () => getInitialFormState(location.query?.email),
    [location.query?.email]
  );

  const [formState, dispatch] = useReducer<typeof reducer>(
    reducer,
    initialFormState
  );

  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'registrationForm',
  });

  const reduxDispatch = useDispatch();

  const handleRegisterSuccess: Parameters<
    typeof useRegister
  >[0]['onSuccess'] = (response) => {
    reduxDispatch({
      type: 'UI__START_ONBOARDING',
      payload: {
        registeredOnCompanyPage: location.type === companyPageType,
      },
    });
    props.onRegister?.();
    try {
      hotjar?.('identify', response.data.data.id, {
        register_date: new Date().toISOString(),
      });
    } catch (err) {}
  };

  const trackRegistrationError = () => trackEvent({ action: 'failure' });

  const {
    mutate: createAccount,
    status: createAccountStatus,
    error: createAccountError,
  } = useRegister({
    onSuccess: handleRegisterSuccess,
    onError: trackRegistrationError,
  });

  const { referralData } = useRewardfulData();

  const isMounted = useIsMounted();

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    createAccount({
      email: formState.email.value,
      password: formState.password.value,
      firstName: formState.firstName.value,
      referralData,
      redirectTo: props.postVerifyRedirectTo,
    });
  };

  const formIsValid =
    formState.email.state === 'valid' &&
    formState.password.state === 'valid' &&
    formState.firstName.state === 'valid';

  const t = useTranslation();

  const formDisabled =
    props.disabled || createAccountStatus === 'pending' || !isMounted;

  const validateEmail = useCallback(
    (email: string) => {
      if (!validateEmailAddress(email) && email !== '') {
        dispatch({
          type: 'emailValidation',
          value: t('app.modal.registration.form.email.validation'),
        });
      } else {
        dispatch({ type: 'emailValidation', value: '' });
      }
    },
    [t]
  );

  useEffect(() => {
    validateEmail(initialFormState.email?.value);
  }, [initialFormState.email?.value]);

  const passwordSchema = useMemo(
    () =>
      z
        .string()
        .min(10, {
          message: t(
            'app.modal.registration.form.password.complex.validation.length'
          ),
        })
        .max(100, {
          message: t(
            'app.modal.registration.form.password.complex.validation.length.max'
          ),
        })
        .regex(
          // String contains 3 out of the 4 following character types;
          // lowercase
          // uppercase
          // non alphabet characters
          // number
          new RegExp(
            /^((?=.*[\d])(?=.*[a-z])(?=.*[A-Z])|(?=.*[a-z])(?=.*[A-Z])(?=.*[^\w\d\s])|(?=.*[\d])(?=.*[A-Z])(?=.*[^\w\d\s])|(?=.*[\d])(?=.*[a-z])(?=.*[^\w\d\s])).*$/
          ),
          {
            message: t(
              'app.modal.registration.form.password.complex.validation.characters'
            ),
          }
        ),
    [t]
  );

  return (
    <Track>
      {createAccountStatus === 'error' && (
        <AuthenticationErrorMessages
          error={createAccountError}
          email={formState.email.value}
        />
      )}
      <form onSubmit={handleSubmit} method="POST">
        <FieldWrapper>
          <Textfield
            name="emailAddress"
            error={formState.email.errorMessage}
            onBlur={() => {
              validateEmail(formState.email.value);
            }}
            onChange={(e) => {
              dispatch({ type: 'editEmail', value: e.currentTarget.value });
            }}
            disabled={formDisabled}
            type="email"
            label={t('app.modal.registration.form.email.label')}
            placeholder={t('app.modal.registration.form.email.placeholder')}
            value={formState.email.value}
            postfix={
              <If
                conditions={{
                  valid: formState.email.state === 'valid',
                }}
              >
                {{
                  valid: () => <Valid data-cy-id="emailAddress-check-icon" />,
                }}
              </If>
            }
            styleType="standardReverse"
          />
        </FieldWrapper>
        <FieldWrapper>
          <Textfield
            name="password"
            error={formState.password.errorMessage}
            onBlur={() => {
              const result = passwordSchema.safeParse(formState.password.value);

              if (result.success) {
                dispatch({ type: 'passwordValidation', value: '' });
                return;
              }

              dispatch({
                type: 'passwordValidation',
                value: result.error.issues[0].message,
              });
              return;
            }}
            onChange={(e) => {
              dispatch({ type: 'editPassword', value: e.currentTarget.value });

              // Live validation added for complex requirements as it gives faster feedback to users.
              const result = passwordSchema.safeParse(e.currentTarget.value);

              if (result.success) {
                dispatch({ type: 'passwordValidation', value: '' });
                return;
              }

              dispatch({
                type: 'passwordValidation',
                value: result.error.issues[0].message,
              });
              return;
            }}
            disabled={formDisabled}
            type="password"
            placeholder={t('app.modal.registration.form.password.placeholder')}
            label={t('app.modal.registration.form.password.label')}
            value={formState.password.value}
            postfix={
              formState.password.state === 'valid' ? (
                <Valid data-cy-id="password-check-icon" />
              ) : null
            }
            styleType="standardReverse"
          />
          <PasswordRequirement>
            {formState.password.errorMessage === undefined ||
            formState.password.errorMessage?.length === 0
              ? t('app.modal.registration.form.password.complex.requirement')
              : ''}
          </PasswordRequirement>
        </FieldWrapper>
        <FieldWrapper>
          <Textfield
            name="firstname"
            onChange={(e) =>
              dispatch({ type: 'editName', value: e.currentTarget.value })
            }
            disabled={formDisabled}
            type="text"
            label={t('app.modal.registration.form.firstName.label')}
            placeholder={t('app.modal.registration.form.firstName.placeholder')}
            value={formState.firstName.value}
            styleType="standardReverse"
          />
        </FieldWrapper>
        <Button
          styleType="louderLarge"
          display="block"
          disabled={formDisabled || !formIsValid}
          isLoading={createAccountStatus === 'pending'}
          type="submit"
          data-cy-id="button-submit-register"
        >
          {t('app.modal.registration.form.submit.label')}
        </Button>
      </form>
    </Track>
  );
};
